import * as types from '../types';
import { IBundlesApi } from '../contracts';
import { HttpApi } from './HttpApi';
import { withJsonErrorBody } from './attachments';
import { filenameFromDisposition } from './importExport';

const enc = encodeURIComponent;

/** Only the filters that are set go on the query string (the server rejects blanks). */
function params(query: object): Record<string, string | number> {
  const result: Record<string, string | number> = {};
  Object.entries(query as Record<string, string | number | boolean | undefined>).forEach(([key, value]) => {
    if (value === undefined || value === '') return;
    result[key] = typeof value === 'boolean' ? String(value) : value;
  });
  return result;
}

/** Service bundles, enrollments, renewals and alert routing (tracker item 85). */
export class BundlesHttpApi extends HttpApi implements IBundlesApi {
  async listServiceBundles(filters: types.ServiceBundleListFilters = {}): Promise<types.ServiceBundle[]> {
    return (await this.http.get<types.APIResponse<types.ServiceBundle[]>>('/service-bundles', { params: params(filters) })).data.data;
  }

  async getServiceBundle(id: string): Promise<types.ServiceBundle> {
    return (await this.http.get<types.APIResponse<types.ServiceBundle>>(`/service-bundles/${enc(id)}`)).data.data;
  }

  async createServiceBundle(data: types.CreateServiceBundleRequest): Promise<types.ServiceBundle> {
    return (await this.http.post<types.APIResponse<types.ServiceBundle>>('/service-bundles', data)).data.data;
  }

  async updateServiceBundle(id: string, data: types.UpdateServiceBundleRequest): Promise<types.ServiceBundle> {
    return (await this.http.put<types.APIResponse<types.ServiceBundle>>(`/service-bundles/${enc(id)}`, data)).data.data;
  }

  async deleteServiceBundle(id: string): Promise<void> {
    await this.http.delete(`/service-bundles/${enc(id)}`);
  }

  async previewServiceBundle(id: string, data: { propertyId: string; startDate: string }): Promise<types.BundlePreview> {
    return (await this.http.post<types.APIResponse<types.BundlePreview>>(`/service-bundles/${enc(id)}/preview`, data)).data.data;
  }

  async listEnrollments(filters: types.EnrollmentListFilters = {}): Promise<types.EnrollmentPage> {
    return (await this.http.get<types.EnrollmentPage>('/enrollments', { params: params(filters) })).data;
  }

  async listPropertyEnrollments(propertyId: string, filters: types.EnrollmentListFilters = {}): Promise<types.EnrollmentPage> {
    return (await this.http.get<types.EnrollmentPage>(`/properties/${enc(propertyId)}/enrollments`, { params: params(filters) })).data;
  }

  async getEnrollment(id: string): Promise<types.BundleEnrollment> {
    return (await this.http.get<types.APIResponse<types.BundleEnrollment>>(`/enrollments/${enc(id)}`)).data.data;
  }

  async addBundleToProperty(propertyId: string, data: types.AddBundleRequest): Promise<types.CreatedEnrollment> {
    return (await this.http.post<types.APIResponse<types.CreatedEnrollment>>(`/properties/${enc(propertyId)}/enrollments`, data)).data.data;
  }

  async updateEnrollmentAutoRenew(id: string, autoRenew: boolean): Promise<types.BundleEnrollment> {
    return (await this.http.patch<types.APIResponse<types.BundleEnrollment>>(`/enrollments/${enc(id)}`, { autoRenew })).data.data;
  }

  async cancelEnrollment(id: string, reason?: string): Promise<types.BundleEnrollment> {
    return (await this.http.post<types.APIResponse<types.BundleEnrollment>>(`/enrollments/${enc(id)}/cancel`, reason ? { reason } : {})).data.data;
  }

  async updateEnrollmentStepPrice(id: string, step: number, data: types.StepPriceRequest): Promise<types.BundleEnrollment> {
    return (await this.http.patch<types.APIResponse<types.BundleEnrollment>>(`/enrollments/${enc(id)}/steps/${step}/price`, data)).data.data;
  }

  async getEnrollmentQuote(id: string, startDate?: string): Promise<types.RenewalQuote> {
    return (await this.http.get<types.APIResponse<types.RenewalQuote>>(`/enrollments/${enc(id)}/quote`, { params: params({ startDate }) })).data.data;
  }

  async createRenewal(id: string, startDate?: string): Promise<types.RenewalCreateResult> {
    return (await this.http.post<types.APIResponse<types.RenewalCreateResult>>(`/enrollments/${enc(id)}/renewals`, startDate ? { startDate } : {})).data.data;
  }

  async createRenewalsBulk(enrollmentIds: string[], startDate?: string): Promise<types.BulkRenewalResult> {
    return (await this.http.post<types.APIResponse<types.BulkRenewalResult>>('/enrollments/renewals/bulk', { enrollmentIds, ...(startDate ? { startDate } : {}) })).data.data;
  }

  async listRenewals(filters: types.RenewalListFilters = {}): Promise<types.RenewalPage> {
    return (await this.http.get<types.RenewalPage>('/enrollments/renewals', { params: params(filters) })).data;
  }

  async exportRenewals(filters: types.RenewalListFilters = {}): Promise<types.CsvDownload> {
    try {
      // The export covers every page, so the paging fields are left off
      const response = await this.http.get<Blob>('/enrollments/renewals/export', { responseType: 'blob', params: params({ ...filters, page: undefined, limit: undefined }), timeout: 120_000 });
      return { blob: response.data, filename: filenameFromDisposition(response.headers?.['content-disposition'], 'renewals.csv') };
    } catch (error) {
      throw await withJsonErrorBody(error);
    }
  }

  async approveEnrollment(id: string, data: types.ApproveEnrollmentRequest = {}): Promise<types.BundleEnrollment> {
    return (await this.http.post<types.APIResponse<types.BundleEnrollment>>(`/enrollments/${enc(id)}/approve`, data)).data.data;
  }

  async declineEnrollment(id: string, reason?: string): Promise<types.BundleEnrollment> {
    return (await this.http.post<types.APIResponse<types.BundleEnrollment>>(`/enrollments/${enc(id)}/decline`, reason ? { reason } : {})).data.data;
  }

  async listPendingEnrollments(filters: types.PendingEnrollmentFilters = {}): Promise<types.PendingEnrollmentPage> {
    return (await this.http.get<types.PendingEnrollmentPage>('/enrollments/pending', { params: params(filters) })).data;
  }

  async getBundleAlertRouting(): Promise<types.BundleAlertRoutingResponse> {
    return (await this.http.get<types.APIResponse<types.BundleAlertRoutingResponse>>('/settings/bundle-alerts')).data.data;
  }

  async updateBundleAlertRouting(routing: Partial<types.BundleAlertRouting>): Promise<types.BundleAlertRoutingResponse> {
    return (await this.http.put<types.APIResponse<types.BundleAlertRoutingResponse>>('/settings/bundle-alerts', { routing })).data.data;
  }
}
